import { Button } from '@synoptic/ui-kit/button/button.js';
import { IconButton } from '@synoptic/ui-kit/button/icon-button.js';
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from '@synoptic/ui-kit/collapsible/collapsible.js';
import { PlusIcon } from '@synoptic/ui-kit/icons/react/plus.js';
import { TrashIcon } from '@synoptic/ui-kit/icons/react/trash.js';
import { Input } from '@synoptic/ui-kit/input/input.js';
import { useIsTablet } from '@synoptic/ui-kit/responsive/hooks.js';
import { Switch } from '@synoptic/ui-kit/switch/switch.js';
import clsx from 'clsx';
import { PropsWithChildren } from 'react';
import { Controller, useFieldArray, useForm } from 'react-hook-form';
import {
  CustomCollectionFilterConfig,
  Stream,
} from '@/__generated__/schema-types';
import { CollapsibleLabel } from '@/components/collapsible-label/collapsible-label';
import { useMe } from '@/routes/_app/use-me';
import { AIFilters } from '../ai-filters/ai-filters';
import { DisabledAIFilters } from '../ai-filters/disabled-ai-filters';
import { TryAIFilters } from '../ai-filters/try-ai-filters';
import { ColorPicker } from './color-picker';
import { COLORS } from './color-picker-colors.ts';
import { CustomTargetsSection } from './custom-targets-section/custom-targets-section';
import { SourceCustomTargetsMap } from './custom-targets-section/source-custom-targets';
import {
  KeywordFiltersSummary,
  RegexFilterSummary,
} from './filters-summary/filters-summary';
import {
  REGEX_HINT_DESCRIPTION,
  REGEX_HINT_TITLE,
  RegexHelpAction,
} from './regex-help-hint.tsx';
import { TypeaheadMultiselect } from './typeahead-multiselect/typeahead-multiselect';
import { formFieldDescription } from '../ai-filters/manage-filter/manage-filter.css';
import {
  createBtn,
  listForm,
  regexInput,
  contentContainer,
  group,
  expandableGroup,
  expandableSwitchGroupLabel,
  divider,
  highlightValueContainer,
  highlightValueInputContainer,
  addHighlightButton,
  filterInput,
} from './list-form.css';

export type ListFormInputs = {
  name: string;
  sources: Stream[];
  /** Per-stream subset of custom targets; missing/empty entry = all targets */
  sourceCustomTargets?: SourceCustomTargetsMap;
  filters: CustomCollectionFilterConfig;
  highlights?: {
    isEnabled: boolean;
    values: Array<{ regex: string; color: string }>;
  } | null;
};

interface ListFormProps {
  initialValues?: ListFormInputs;
  collectionId?: string;
  submit: (form: ListFormInputs) => void;
  isSubmitLoading?: boolean;
  submitText?: string;
}

const MAX_SOURCES = 100;

function parseUserRegex(userInput: string) {
  const match = userInput.match(/^\/(.+)\/([a-z]*)$/i);

  if (!match) {
    throw new Error();
  }

  const pattern = match[1];
  const flags = match[2];

  return new RegExp(pattern, flags);
}

const filterValidation = {
  maxLength: {
    value: 1000,
    message: 'Filter cannot exceed 1000 characters',
  },
};

export const ListForm = ({
  initialValues,
  collectionId,
  submit,
  isSubmitLoading,
  submitText = 'Create',
  children,
}: PropsWithChildren<ListFormProps>) => {
  const isTablet = useIsTablet();
  const { protectedFields } = useMe();

  const isOpenAIKeySet = !!protectedFields?.openAIKey;
  const isOpenAIKeyDisabled = protectedFields?.openAIKey?.enabled === false;

  const initialFilters = initialValues?.filters || {};

  const initialHighlightValues =
    initialValues?.highlights?.values &&
    initialValues?.highlights?.values?.length > 0
      ? initialValues?.highlights?.values?.map((value) => ({
          regex: value.regex,
          color: value.color,
        }))
      : [
          {
            regex: '',
            color: 'green',
          },
        ];

  const { handleSubmit, formState, register, control, watch } =
    useForm<ListFormInputs>({
      mode: 'onChange',
      shouldFocusError: true,
      defaultValues: {
        name: initialValues?.name || '',
        sources: initialValues?.sources || [],
        sourceCustomTargets: initialValues?.sourceCustomTargets || {},
        filters: {
          includeKeywords: {
            all: initialFilters.includeKeywords?.all || '',
            any: initialFilters.includeKeywords?.any || '',
            phrase: initialFilters.includeKeywords?.phrase || '',
          },
          excludeKeywords: {
            all: initialFilters.excludeKeywords?.all || '',
            any: initialFilters.excludeKeywords?.any || '',
            phrase: initialFilters.excludeKeywords?.phrase || '',
          },
          ai: initialFilters.ai || null,
          regex: initialFilters.regex || '',
          isHighlightEnabled: !!initialFilters.isHighlightEnabled,
          highlightColor: initialFilters.highlightColor || 'lightYellow',
        },
        highlights: {
          isEnabled: initialValues?.highlights?.isEnabled || false,
          values: initialHighlightValues,
        },
      },
    });

  const {
    fields: highlightFields,
    append,
    remove,
  } = useFieldArray({
    control,
    name: 'highlights.values',
  });

  const selectedSources = watch('sources');

  return (
    <form
      className={listForm}
      onSubmit={handleSubmit((formValues) => {
        submit({
          ...formValues,
          highlights: {
            isEnabled: formValues.highlights?.isEnabled || false,
            values:
              formValues.highlights?.values?.filter(
                (value) => value.regex.trim() !== '',
              ) || [],
          },
        });
      })}
    >
      <Input
        label="Name your list"
        placeholder="ex. world news"
        errorMessage={formState.errors.name?.message}
        type="text"
        {...register('name', {
          required: 'This field is required',
          maxLength: {
            value: 30,
            message: 'Name cannot exceed 30 characters',
          },
          validate: (v) => v.trim().length > 0,
        })}
        autoFocus
      />
      <Controller
        name="sources"
        control={control}
        rules={{
          required: 'This field is required',
          validate: (value) => {
            return value.length > MAX_SOURCES
              ? `You can only add up to ${MAX_SOURCES} streams to a list`
              : true;
          },
        }}
        render={({ field }) => {
          return (
            <TypeaheadMultiselect
              selectedSources={field.value}
              onSourcesChange={field.onChange}
              errorMessage={formState.errors.sources?.message}
            />
          );
        }}
      />
      <Controller
        name="sourceCustomTargets"
        control={control}
        render={({ field }) => (
          <CustomTargetsSection
            sources={selectedSources}
            value={field.value || {}}
            onChange={field.onChange}
            collectionId={collectionId}
          />
        )}
      />
      <div className={clsx(contentContainer, group)}>
        <CollapsibleLabel
          title="Include filters"
          summary={
            <KeywordFiltersSummary
              control={control}
              name="filters.includeKeywords"
            />
          }
          testId={'include-filters'}
        >
          <Input
            label="All of these words"
            placeholder='Example: earnings forecast — include posts with "earnings" and "forecast"'
            errorMessage={
              formState.errors.filters?.includeKeywords?.all?.message
            }
            className={filterInput}
            {...register('filters.includeKeywords.all', filterValidation)}
          />
          <Input
            label="Any of these words"
            placeholder='Example: SPY QQQ — include posts with "SPY" or "QQQ" (or both)'
            errorMessage={
              formState.errors.filters?.includeKeywords?.any?.message
            }
            className={filterInput}
            {...register('filters.includeKeywords.any', filterValidation)}
          />
          <Input
            label="This exact phrase"
            placeholder='Example: buy the dip — include all posts with this phrase "buy the dip"'
            errorMessage={
              formState.errors.filters?.includeKeywords?.phrase?.message
            }
            className={filterInput}
            {...register('filters.includeKeywords.phrase', filterValidation)}
          />
        </CollapsibleLabel>
        <CollapsibleLabel
          title="Exclude filters"
          summary={
            <KeywordFiltersSummary
              control={control}
              name="filters.excludeKeywords"
            />
          }
          testId={'exclude-filters'}
        >
          <Input
            label="All of these words"
            placeholder='Example: crypto NFT — exclude posts with "crypto" and "NFT"'
            errorMessage={
              formState.errors.filters?.excludeKeywords?.all?.message
            }
            className={filterInput}
            {...register('filters.excludeKeywords.all', filterValidation)}
          />
          <Input
            label="Any of these words"
            placeholder='Example: rumor leak — exclude posts with "rumor" or "leak"'
            errorMessage={
              formState.errors.filters?.excludeKeywords?.any?.message
            }
            className={filterInput}
            {...register('filters.excludeKeywords.any', filterValidation)}
          />
          <Input
            label="This exact phrase"
            placeholder='Example: financial advisor — exclude all posts containing this phrase "financial advisor"'
            errorMessage={
              formState.errors.filters?.excludeKeywords?.phrase?.message
            }
            className={filterInput}
            {...register('filters.excludeKeywords.phrase', filterValidation)}
          />
        </CollapsibleLabel>
        <CollapsibleLabel
          title="Regex"
          description="advanced"
          summary={<RegexFilterSummary control={control} />}
        >
          <>
            <Input
              label="Enter a regex pattern"
              hintTitle={REGEX_HINT_TITLE}
              hint={REGEX_HINT_DESCRIPTION}
              hintAction={<RegexHelpAction />}
              placeholder="ex: /[A-Z]\w+/"
              containerClassName={regexInput}
              errorMessage={formState.errors.filters?.regex?.message}
              {...register('filters.regex', {
                validate: (regexString) => {
                  if (!regexString) {
                    return true;
                  }
                  try {
                    parseUserRegex(regexString);
                    return true;
                  } catch {
                    return 'Invalid regex pattern. Please check the syntax.';
                  }
                },
              })}
            />
            {!formState.errors.filters?.regex && (
              <p className={formFieldDescription}>
                Use regex to filter results based on patterns.
              </p>
            )}
          </>
        </CollapsibleLabel>
        {!isOpenAIKeySet && <TryAIFilters />}
        {isOpenAIKeyDisabled && protectedFields?.openAIKey?.key && (
          <DisabledAIFilters openAIKey={protectedFields.openAIKey} />
        )}
        {isOpenAIKeySet && !isOpenAIKeyDisabled && (
          <Controller
            name="filters.ai"
            control={control}
            render={({ field }) => (
              <AIFilters
                onSelectFilter={field.onChange}
                selectedFilter={field.value}
              />
            )}
          />
        )}
        <Controller
          name="filters.isHighlightEnabled"
          control={control}
          render={({ field }) => (
            <Collapsible
              open={field.value || false}
              data-testid="highlight filter results"
            >
              <CollapsibleTrigger asChild>
                <label className={expandableSwitchGroupLabel}>
                  Highlight filter results
                  <Switch
                    checked={field.value || false}
                    onCheckedChange={field.onChange}
                  />
                </label>
              </CollapsibleTrigger>
              <CollapsibleContent>
                <div className={expandableGroup}>
                  <Controller
                    name="filters.highlightColor"
                    control={control}
                    render={({ field }) => (
                      <ColorPicker
                        label="Highlight color"
                        selectedColor={field.value}
                        onColorSelect={field.onChange}
                      />
                    )}
                  />
                </div>
              </CollapsibleContent>
            </Collapsible>
          )}
        />
      </div>

      <Controller
        name="highlights.isEnabled"
        control={control}
        render={({ field }) => (
          <Collapsible
            open={field.value || false}
            data-testid="highlight text"
            className={group}
          >
            <CollapsibleTrigger asChild>
              <label className={expandableSwitchGroupLabel}>
                Highlight text
                <Switch
                  checked={field.value || false}
                  onCheckedChange={field.onChange}
                />
              </label>
            </CollapsibleTrigger>
            <CollapsibleContent>
              <div className={expandableGroup}>
                {highlightFields.map((highlightField, index) => (
                  <div key={highlightField.id}>
                    {index > 0 && <div className={divider} />}
                    <div className={highlightValueContainer}>
                      <div className={highlightValueInputContainer}>
                        <Input
                          label="Enter a highlight regex pattern"
                          hintTitle={REGEX_HINT_TITLE}
                          hint={REGEX_HINT_DESCRIPTION}
                          hintAction={<RegexHelpAction />}
                          placeholder="ex: /[A-Z]\w+/"
                          errorMessage={
                            formState.errors?.highlights?.values?.[index]?.regex
                              ?.message
                          }
                          {...register(`highlights.values.${index}.regex`, {
                            validate: (regexString) => {
                              if (!field.value) {
                                return true;
                              }
                              if (!regexString) {
                                return 'This field is required';
                              }
                              try {
                                parseUserRegex(regexString);
                                return true;
                              } catch {
                                return 'Invalid regex pattern. Please check the syntax.';
                              }
                            },
                          })}
                        />
                      </div>
                      {highlightFields.length > 1 && (
                        <IconButton
                          variant="ghost"
                          size="small"
                          aria-label="Remove highlight"
                          onClick={() => remove(index)}
                        >
                          <TrashIcon />
                        </IconButton>
                      )}
                    </div>
                    <Controller
                      name={`highlights.values.${index}.color`}
                      control={control}
                      render={({ field }) => (
                        <ColorPicker
                          label="Highlight color"
                          selectedColor={field.value}
                          onColorSelect={field.onChange}
                        />
                      )}
                    />
                  </div>
                ))}
                {highlightFields.length < COLORS.length && (
                  <Button
                    variant="ghost"
                    size="small"
                    className={addHighlightButton}
                    onClick={() => append({ regex: '', color: 'green' })}
                    startIcon={<PlusIcon />}
                  >
                    Add highlight
                  </Button>
                )}
              </div>
            </CollapsibleContent>
          </Collapsible>
        )}
      />
      {children}
      <Button
        loading={isSubmitLoading || formState.isSubmitting}
        className={createBtn}
        disabled={
          Boolean(formState.errors.name) || Boolean(formState.errors.sources)
        }
        type="submit"
        size={isTablet ? 'large' : 'medium'}
      >
        {submitText}
      </Button>
    </form>
  );
};
